import { Crown } from 'lucide-react'
import type { ReactNode } from 'react'

export interface StandingRow {
  id: string
  nickname: string
  isHost: boolean
  online: boolean
  /** Right-aligned status: a place, "12 mv", "crashed"… */
  status: ReactNode
  score: number
}

interface Props {
  /** Already sorted by the game. */
  rows: StandingRow[]
  me: string | null
  /** `list` is the sidebar table; `strip` is a horizontally scrolling row for phones. */
  layout: 'list' | 'strip'
  /** The figure or avatar on the left of a row (the strip usually shows the drawn figure). */
  figure: (row: StandingRow, layout: 'list' | 'strip') => ReactNode
}

export function Standings({ rows, me, layout, figure }: Props) {
  const strip = layout === 'strip'
  return (
    <ul
      className={
        strip ? 'flex w-full max-w-[400px] gap-2 overflow-x-auto pb-1' : 'flex flex-col gap-1.5'
      }
      aria-label="Standings"
    >
      {rows.map((r) => {
        const mine = r.id === me
        return (
          <li
            key={r.id}
            className={[
              'flex items-center gap-2 rounded-xl',
              strip
                ? 'shrink-0 bg-white px-2 py-1 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800'
                : 'px-2 py-1.5',
              mine ? (strip ? 'ring-2 ring-sky-500' : 'bg-slate-100 dark:bg-slate-800') : '',
            ].join(' ')}
          >
            {figure(r, layout)}
            <span
              className={`min-w-0 truncate text-sm font-medium ${strip ? 'max-w-24' : 'flex-1'}`}
            >
              {r.nickname}
              {r.isHost && (
                <Crown className="ml-1 inline size-3.5 text-amber-500" aria-label="host" />
              )}
              {!r.online && !strip && <span className="ml-1 text-xs text-slate-400">offline</span>}
            </span>
            <span className="text-xs tabular-nums text-slate-500">{r.status}</span>
            <span className="w-7 text-right text-sm font-bold tabular-nums">{r.score}</span>
          </li>
        )
      })}
    </ul>
  )
}
